import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { Portal } from "solid-js/web";
import type { CargoSegment, ItineraryStop, VoyageDetails } from "../../shared/api/types";
import type { VoyageStopOverride } from "../../shared/api/endpoints";
import { formatNumber } from "../../shared/locale";
import {
  aggregateOverride,
  type CargoSummary,
  dischargeAt,
  type EffectiveTotals,
  effectiveSummary,
  fmtCbm,
  fmtLocal,
  fmtUnits,
  fmtWeight,
  formatSession,
  groupItineraries,
  loadAt,
  pickHighlightedStopOrders,
  portFuncLabel,
  segmentSummary,
  sessionSummary,
  stopSymbols,
} from "./details-format";
import s from "./ItinerarySection.module.css";
import { resolveOverrides, suggestReattachStop } from "./override-identity";
import { StopOverrideDialog } from "./StopOverrideDialog";
import { useVoyageDetails } from "./store";
import { TextTooltip } from "../../shared/inputs/TextTooltip";
import { isHiddenStopVisible } from "../calendar/icon-position";
import { SearchableSelect } from "../settings/SearchableSelect";

// Per-stop port-function badges (.port-func-L, .port-func-D, …) use a
// gradient background in global.css. The tooltip stripe picks the
// brighter end of that gradient so the hover accent matches what the
// user sees on the badge itself.
function portFuncStripe(code: string | null | undefined): string | undefined {
  switch (code) {
    case "L": return "var(--load-fg-bright)";
    case "D": return "var(--disch-fg-bright)";
    case "F": return "var(--bunker-fg-bright)";
    case "I": return "var(--transit-fg-bright)";
    default: return undefined;
  }
}

export interface ItinerarySectionProps {
  details: VoyageDetails;
  currentPortName: string | null;
  // Selected calendar bar's whole-port-stay window. Used to disambiguate
  // when the vessel calls the same terminal more than once in this voyage
  // (schedule API emits per-call entries → multiple bars). Omitted by the
  // vessels-tab surface, which has no per-call context.
  currentArrivalLocal?: string | null;
  currentDepartureLocal?: string | null;
  portColors: Record<string, string>;
  // Driven by DetailsBody, which renders the "Show all stops / Hide
  // transit stops" toggle in the section title row so it can sit
  // alongside the heading instead of in its own band above the list.
  showAllStops: boolean;
}

interface CargoPopup {
  code: string;
  groupName: string;
  kind: "load" | "discharge";
  summary: CargoSummary;
  // Aggregated overrides across the group's stops (null when nothing
  // is overridden). The popup uses it to render the units / cbm /
  // weight totals as strikethrough-original → new. cbm + weight scale
  // proportionally with the unit override per code (effectiveSummary).
  override: EffectiveTotals | null;
}

export function ItinerarySection(props: ItinerarySectionProps) {
  const groups = createMemo(() =>
    groupItineraries(props.details.itineraries ?? []));

  // Stops that belong to operational call categories (A/B/C/D/L) are
  // shown by default — transit/admin codes (P/W/F/I/T/Y/Z/R/O/S) hide
  // unless the user flips the DetailsBody-owned toggle. Same allow-
  // list as the journey-connection layer's isHiddenStopVisible filter.
  function visibleStops<T extends { portFunc: string }>(stops: readonly T[]): T[] {
    if (props.showAllStops) return [...stops];
    return stops.filter((stp) => isHiddenStopVisible(stp.portFunc));
  }

  // Orders of itinerary stops the selected bar represents — narrows the
  // open-group + highlighted-stop behaviour to one entry when the vessel
  // visits the same terminal twice in this voyage. See
  // pickHighlightedStopOrders for the fallback rules.
  const highlighted = () =>
    pickHighlightedStopOrders(props.details.itineraries ?? [], {
      portName: props.currentPortName,
      arrivalLocal: props.currentArrivalLocal,
      departureLocal: props.currentDepartureLocal,
    });

  const vd = useVoyageDetails();
  const overrides = () => vd.getOverrides(props.details.vesselCode, props.details.voyageNo);
  // Bind overrides to stops by content identity (terminal + portFunc +
  // occurrence + dummySource), not positional stopOrder — a re-cut
  // itinerary can shift stopOrder values, which previously landed an
  // override on the wrong terminal.
  // Resolve once per (overrides, itinerary) change — bound() and
  // orphans() below both derive from it. Without the memo, overrideForStop
  // (called per stop for the dot + per group for the cargo aggregates)
  // would re-run resolveOverrides for every stop.
  const resolved = createMemo(() =>
    resolveOverrides(overrides(), props.details.itineraries ?? []),
  );
  const bound = createMemo(() => {
    const map = new Map<ItineraryStop, VoyageStopOverride>();
    for (const b of resolved().bindings) map.set(b.stop, b.override);
    return map;
  });
  function overrideForStop(stop: ItineraryStop) {
    return bound().get(stop) ?? null;
  }

  // Saved overrides whose stamped identity no longer resolves to any
  // live stop (upstream re-cut the itinerary — the terminal call the
  // override was on is gone). Surfaced as a callout above the list so
  // the user can move the saved working times onto a current stop or
  // drop them outright.
  const orphans = () => resolved().orphans;
  // Reattach targets are restricted to Load / Discharge stops — the
  // override content (stevedore sessions, volumes) only makes sense
  // pinned to a working call, not a transit/admin stop.
  const workingStops = () => (props.details.itineraries ?? []).filter((stp) => stp.portFunc === "L" || stp.portFunc === "D");
  function stopValue(stp: ItineraryStop): string {
    const ds = stp.isDummy ? (stp.dummySource === "previous" || stp.dummySource === "next" ? stp.dummySource : "") : "";
    return `${stp.order}|${ds}`;
  }
  function stopLabel(stp: ItineraryStop): string {
    return `${stp.portName} · ${stp.portFunc} · arr ${fmtLocal(stp.arrivalLocal)}`;
  }
  const [popup, setPopup] = createSignal<CargoPopup | null>(null);
  const [editStop, setEditStop] = createSignal<ItineraryStop | null>(null);
  function closePopup() { setPopup(null); }
  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      // Stop dialog owns its own ESC handler; only close the cargo popup
      // here so a single ESC doesn't bubble through and close both at once.
      if (!editStop()) closePopup();
    }
  }
  onMount(() => document.addEventListener("keydown", onKey));
  onCleanup(() => document.removeEventListener("keydown", onKey));
  // Reset the popup if a different voyage is loaded — its cargo will be
  // different and the open popup would otherwise show stale data.
  createEffect(() => {
    void props.details;
    setPopup(null);
    setEditStop(null);
  });

  // Scroll the highlighted ".itin-current" stop into view whenever the
  // selected port or the underlying details change. Mirrors OLD's
  // requestAnimationFrame(scrollIntoView) — long itineraries can push
  // the matching stop off-screen otherwise. block:"center" keeps it
  // surrounded by context.
  let rootEl: HTMLDivElement | undefined;
  createEffect(() => {
    // Track the deps so the effect re-fires.
    void props.currentPortName;
    void props.currentArrivalLocal;
    void props.currentDepartureLocal;
    void props.details;
    requestAnimationFrame(() => {
      const cur = rootEl?.querySelector<HTMLElement>(".itin-stop.itin-current");
      cur?.scrollIntoView({ behavior: "auto", block: "center" });
    });
  });

  return (
    <div ref={(el) => (rootEl = el)}>
    <Show when={orphans().length > 0}>
      <div class={s.orphanCallout}>
        <div class={s.orphanHead}>⚠ {orphans().length} saved override{orphans().length === 1 ? "" : "s"} no longer match a stop</div>
        <For each={orphans()}>
          {(o) => {
            const suggested = suggestReattachStop(o, props.details.itineraries ?? []);
            const [target, setTarget] = createSignal(suggested ? stopValue(suggested) : "");
            const targetStop = () => workingStops().find((stp) => stopValue(stp) === target());
            return (
              <div class={s.orphanRow}>
                <div class={s.orphanMeta}>
                  <span class={s.orphanTerm}>{o.portExternalRef ? `Was attached to ${o.portExternalRef}` : "Saved override (terminal not recorded)"}</span>
                  <Show
                    when={o.stevedoreSessions && o.stevedoreSessions.length > 0}
                    fallback={<span class={s.orphanSub}>{sessionSummary(o)}</span>}
                  >
                    <div class={s.orphanSessions}>
                      <For each={o.stevedoreSessions!}>
                        {(sess) => <span class={s.orphanSession}>{formatSession(sess)}</span>}
                      </For>
                    </div>
                  </Show>
                </div>
                <SearchableSelect
                  value={target}
                  setValue={setTarget}
                  options={() => workingStops().map((stp) => ({ value: stopValue(stp), label: stopLabel(stp) }))}
                  placeholder="Reattach to…"
                />
                <button
                  type="button"
                  class={s.orphanReattach}
                  disabled={!targetStop()}
                  onClick={() => {
                    const stp = targetStop();
                    if (stp) void vd.reattachOverride(props.details.vesselCode, props.details.voyageNo, o, stp, props.details.itineraries ?? []);
                  }}
                >
                  Reattach
                </button>
                <TextTooltip text="Delete override" description="Remove these saved working times" stripe="#ef4444">
                  <button
                    type="button"
                    class={s.orphanDelete}
                    onClick={() => void vd.deleteOverride(props.details.vesselCode, props.details.voyageNo, o)}
                  >
                    Delete
                  </button>
                </TextTooltip>
              </div>
            );
          }}
        </For>
      </div>
    </Show>
    <Show when={groups().length > 0} fallback={<div class="muted">No itinerary stops.</div>}>
      <For each={groups()}>
        {(g) => {
          // Reactive: a group is current when any of its stops is in the
          // currently-highlighted set (which narrows per-call when the
          // selection carries arrival/departure window context).
          // Same dummy guard as the per-stop `isCurrentStop`: a dummy
          // anchor borrowed from the previous/next voyage shares its
          // `order` with one of THIS voyage's real stops, so a plain
          // `highlighted().has(stp.order)` would also auto-open the
          // dummy's group (PYEONGTAEK opens when ZEEBRUGGE is selected
          // and both share order=2100).
          const isCurrent = () => g.stops.some((stp) => !stp.isDummy && highlighted().has(stp.order));
          // Filtered stop list — read 3× below (empty-guard, count,
          // list). Memoise so the filter runs once per showAllStops flip
          // instead of on every read.
          const shownStops = createMemo(() => visibleStops(g.stops));
          // Group-code badge inherits its visual treatment from CSS:
          // neutral chrome by default, full indigo→violet→cyan gradient
          // on `.itin-group-current .itin-group-code`. The previous
          // inline style stamped the terminal-palette colour from
          // Settings onto every badge, which overrode that CSS and
          // leaked colours (e.g. DEBRV showing yellow) onto groups
          // that weren't the current one. Terminal palette still
          // drives the calendar bars + lane labels — just not the
          // itinerary chrome.
          const load = () => loadAt(props.details.cargoData, g.code);
          const disch = () => dischargeAt(props.details.cargoData, g.code);
          // Group-level effective totals once overrides are applied —
          // null when the upstream value should be shown unchanged.
          const loadEff = () => effectiveSummary(
            load(),
            aggregateOverride(g.stops, overrideForStop, "load"),
          );
          const dischEff = () => effectiveSummary(
            disch(),
            aggregateOverride(g.stops, overrideForStop, "discharge"),
          );
          return (
            // Hide the group entirely when the filter strips every
            // stop — keeps the section tidy for vessels whose calls
            // are all transit/admin.
            <Show when={shownStops().length > 0}>
            <details class={`itin-group ${isCurrent() ? "itin-group-current" : ""}`} open={isCurrent()}>
              <summary>
                <span class="itin-group-code">{g.code}</span>
                <span class="itin-group-name">{g.name}</span>
                <span class={s.cargoChips}>
                  <Show when={load()}>
                    {(sum) => (
                      <TextTooltip
                        text="Load"
                        description={
                          loadEff()
                            ? `Override: ${fmtUnits(loadEff()!.units)} · was ${fmtUnits(loadEff()!.originalUnits)} — click for details`
                            : `${fmtUnits(sum().units)} · ${fmtCbm(sum().cbm)} · ${fmtWeight(sum().weight)} — click for details`
                        }
                        stripe="var(--load-fg-bright)"
                      >
                        <button
                          type="button"
                          class={`${s.cargoChip} ${s.cargoLoad}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPopup({ code: g.code, groupName: g.name, kind: "load", summary: sum(), override: loadEff() });
                          }}
                        >
                          L{" "}
                          <Show when={loadEff()} fallback={fmtUnits(sum().units)}>
                            {(eff) => (
                              <>
                                <span class={s.cargoChipStrike}>{formatNumber(eff().originalUnits)}</span>
                                {" "}
                                {fmtUnits(eff().units)}
                              </>
                            )}
                          </Show>
                        </button>
                      </TextTooltip>
                    )}
                  </Show>
                  <Show when={disch()}>
                    {(sum) => (
                      <TextTooltip
                        text="Discharge"
                        description={
                          dischEff()
                            ? `Override: ${fmtUnits(dischEff()!.units)} · was ${fmtUnits(dischEff()!.originalUnits)} — click for details`
                            : `${fmtUnits(sum().units)} · ${fmtCbm(sum().cbm)} · ${fmtWeight(sum().weight)} — click for details`
                        }
                        stripe="var(--disch-fg-bright)"
                      >
                        <button
                          type="button"
                          class={`${s.cargoChip} ${s.cargoDisch}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPopup({ code: g.code, groupName: g.name, kind: "discharge", summary: sum(), override: dischEff() });
                          }}
                        >
                          D{" "}
                          <Show when={dischEff()} fallback={fmtUnits(sum().units)}>
                            {(eff) => (
                              <>
                                <span class={s.cargoChipStrike}>{formatNumber(eff().originalUnits)}</span>
                                {" "}
                                {fmtUnits(eff().units)}
                              </>
                            )}
                          </Show>
                        </button>
                      </TextTooltip>
                    )}
                  </Show>
                </span>
                <span class="itin-group-meta muted">{shownStops().length} stop{shownStops().length === 1 ? "" : "s"}</span>
              </summary>
              <ol class="itinerary">
                <For each={shownStops()}>
                  {(stop) => {
                    // Dummies share an order with one of this voyage's real
                    // stops at the boundary; the highlight set is intended
                    // for the real stop, never the borrowed anchor.
                    const isCurrentStop = () => !stop.isDummy && highlighted().has(stop.order);
                    const stopStyle = () => {
                      const c = props.portColors[stop.portName];
                      return c ? { "border-left-color": c } : undefined;
                    };
                    const ovr = () => overrideForStop(stop);
                    const hasOverride = () => ovr() !== null;
                    // Badge symbols: normal stop = portFunc + additional
                    // L/D; dummy = additional L/D only. Static per stop, so
                    // resolve once. Two symbols render as a split badge.
                    const syms = stopSymbols(stop);
                    return (
                      <li class={`itin-stop ${isCurrentStop() ? "itin-current" : ""} ${hasOverride() ? s.stopOverridden : ""}`} style={stopStyle()}>
                        {/* Badge is a left column spanning the whole stop
                            (name + times rows), so two full-size stacked
                            badges fit the two-line height without growing
                            the row. */}
                        <Show
                          when={syms.length > 1}
                          fallback={
                            <TextTooltip
                              text={portFuncLabel(syms[0]) || (syms[0] || "?")}
                              stripe={portFuncStripe(syms[0])}
                            >
                              <span class={`port-func port-func-${syms[0] || "X"}`} aria-label={portFuncLabel(syms[0])}>
                                {syms[0] || "?"}
                              </span>
                            </TextTooltip>
                          }
                        >
                          <span class="port-func-list" aria-label={`Operations: ${syms.join(", ")}`}>
                            <For each={syms}>
                              {(sym) => (
                                <TextTooltip text={portFuncLabel(sym)} stripe={portFuncStripe(sym)}>
                                  <span class={`port-func port-func-${sym}`}>{sym}</span>
                                </TextTooltip>
                              )}
                            </For>
                          </span>
                        </Show>
                        <div class="itin-body">
                          <div class="itin-row">
                            <span class="port-name">{stop.portName}</span>
                            <TextTooltip
                              text={hasOverride() ? "Edit overrides" : "Add overrides"}
                              description={hasOverride()
                                ? "This stop has stevedore / volume overrides"
                                : "Set stevedore window or volume overrides for this stop"}
                            >
                              <button
                                type="button"
                                class={s.stopEdit}
                                aria-label={`Edit overrides for stop ${stop.order} (${stop.portName})`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEditStop(stop);
                                }}
                              >
                                <Show when={hasOverride()} fallback="✎">●</Show>
                              </button>
                            </TextTooltip>
                          </div>
                          <div class="itin-times muted">
                            arr {fmtLocal(stop.arrivalLocal)} · dep {fmtLocal(stop.departureLocal)}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                </For>
              </ol>
            </details>
            </Show>
          );
        }}
      </For>
    </Show>
    <Show when={popup()}>
      {(p) => {
        // Sort by counterpart port so segments going to / coming from the
        // same place sit together. Also pre-compute the per-row counterpart
        // so the JSX can render a soft divider whenever it changes.
        const counterpart = (seg: CargoSegment): string =>
          (p().kind === "load" ? seg.dischargePort : seg.loadPort) ?? "—";
        const ordered = (): CargoSegment[] => {
          return [...p().summary.segments].sort((a, b) => {
            const ca = counterpart(a);
            const cb = counterpart(b);
            if (ca !== cb) return ca.localeCompare(cb);
            return a.segment.localeCompare(b.segment);
          });
        };
        return (
          <Portal>
            <div class={s.cargoBackdrop} onMouseDown={closePopup}>
              <div
                class={`${s.cargoCard} ${p().kind === "load" ? s.cargoCardLoad : s.cargoCardDisch}`}
                role="dialog"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <header class={s.cargoHead}>
                  <div class={s.cargoHeadText}>
                    <span class={s.cargoKind}>{p().kind === "load" ? "Load" : "Discharge"}</span>
                    <h3 class={s.cargoTitle}>
                      {p().groupName} <span class={s.cargoCode}>{p().code}</span>
                    </h3>
                  </div>
                  <button type="button" class={s.cargoClose} aria-label="Close" onClick={closePopup}>×</button>
                </header>
                <div class={s.cargoTotals}>
                  <div class={s.cargoTotal}>
                    <div class={s.cargoTotalValue}>
                      <Show
                        when={p().override}
                        fallback={formatNumber(p().summary.units)}
                      >
                        {(ovr) => (
                          <>
                            <span class={s.cargoTotalStrike}>{formatNumber(ovr().originalUnits)}</span>
                            <span class={s.cargoTotalArrow}>→</span>
                            {formatNumber(ovr().units)}
                          </>
                        )}
                      </Show>
                    </div>
                    <div class={s.cargoTotalLabel}>units</div>
                  </div>
                  <div class={s.cargoTotal}>
                    <div class={`${s.cargoTotalValue} ${p().override ? s.cargoTotalStaleOnly : ""}`}>
                      {formatNumber(Math.round(p().summary.cbm))}
                    </div>
                    <div class={s.cargoTotalLabel}>m³</div>
                  </div>
                  <div class={s.cargoTotal}>
                    <div class={`${s.cargoTotalValue} ${p().override ? s.cargoTotalStaleOnly : ""}`}>
                      {fmtWeight(p().summary.weight)}
                    </div>
                    <div class={s.cargoTotalLabel}>weight</div>
                  </div>
                </div>
                <div class={s.cargoTableWrap}>
                  <table class={s.cargoTable}>
                    <thead>
                      <tr>
                        <th>{p().kind === "load" ? "Discharging at" : "Loaded at"}</th>
                        <th>Segment</th>
                        <th class={s.numericHeader}>Units</th>
                        <th class={s.numericHeader}>m³</th>
                        <th class={s.numericHeader}>Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={ordered()}>
                        {(seg, i) => {
                          const cp = counterpart(seg);
                          const prevCp = i() > 0 ? counterpart(ordered()[i() - 1]!) : null;
                          const newGroup = prevCp !== null && cp !== prevCp;
                          return (
                            <tr class={newGroup ? s.cargoRowGroup : ""}>
                              <td class={s.cargoCode}>{cp}</td>
                              <td>{seg.segment}</td>
                              <td class={s.numeric}>{formatNumber(seg.qty)}</td>
                              <td class={s.numeric}>{formatNumber(seg.cbm, { maximumFractionDigits: 1 })}</td>
                              <td class={s.numeric}>{fmtWeight(seg.weight)}</td>
                            </tr>
                          );
                        }}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Portal>
        );
      }}
    </Show>
    <Show when={editStop()}>
      {(stop) => {
        const cur = () => overrideForStop(stop());
        const load = () => loadAt(props.details.cargoData, stop().port.externalRef.slice(0, 5));
        const disch = () => dischargeAt(props.details.cargoData, stop().port.externalRef.slice(0, 5));
        const dummySource = (): "" | "previous" | "next" =>
          stop().isDummy ? (stop().dummySource === "previous" || stop().dummySource === "next" ? stop().dummySource as "previous" | "next" : "") : "";
        return (
          <StopOverrideDialog
            vesselCode={props.details.vesselCode}
            voyageNo={props.details.voyageNo}
            stopOrder={stop().order}
            dummySource={dummySource()}
            stopLabel={`${stop().portName} · stop ${stop().order}`}
            defaults={{
              arrivalLocal: stop().arrivalLocal,
              departureLocal: stop().departureLocal,
              loadUnits: load()?.units ?? null,
              dischargeUnits: disch()?.units ?? null,
              shiftingUnits: null,
              loadSegments: segmentSummary(load()),
              dischargeSegments: segmentSummary(disch()),
              shiftingSegments: null,
            }}
            current={cur()}
            onSave={(row) => vd.saveStopOverride(
              props.details.vesselCode, props.details.voyageNo, row,
              stop(), props.details.itineraries ?? [],
            )}
            onClose={() => setEditStop(null)}
          />
        );
      }}
    </Show>
    </div>
  );
}
